import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Grid,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  IJobLocations,
  ILocationResponse,
} from "../../../../../types/campaign.types";
import { COLOR_MAP } from "../../../../../constants/ColorNames";

interface IProps {
  data: ILocationResponse | null;
  handleUpdateJobLocations: (selectedLocations: IJobLocations[]) => void;
  jobLocations: IJobLocations[];
}

enum LocationType {
  TOP_CITIES = "topCities",
  REGION_AND_STATES = "regionAndStates",
  INTERNATIONAL_LOCATIONS = "internationalLocations",
}

const locations = [
  { id: LocationType.TOP_CITIES, label: "Top Cities" },
  { id: LocationType.REGION_AND_STATES, label: "Region And States" },
  {
    id: LocationType.INTERNATIONAL_LOCATIONS,
    label: "International Locations",
  },
];

const JobLocationsSelector: React.FC<IProps> = ({
  data,
  jobLocations,
  handleUpdateJobLocations,
}) => {
  const isCheckboxDisabled = jobLocations?.length > 4;
  const [activeList, setActiveList] = useState<string>(LocationType.TOP_CITIES);
  const [activeSubList, setActiveSubList] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const handleActiveList = (listId: string) => {
    setActiveList(listId);
  };

  const handleActiveSubList = (
    payload: { id: number; name: string } | null
  ) => {
    if (payload) {
      setActiveSubList({ id: payload.id, name: payload.name });
    } else {
      setActiveSubList(null);
    }
  };

  const handleSelectLocation = ({
    id,
    name,
    parent,
  }: {
    id: string;
    name: string;
    parent: string;
  }) => {
    const selectedLocation = jobLocations.find(
      (location) => location.id === id
    );
    if (selectedLocation) {
      const updatedLocations = [
        ...jobLocations.filter((location) => location.id !== id),
      ];
      handleUpdateJobLocations(updatedLocations);
    } else {
      const updatedLocations = [...jobLocations, { id, name, parent }];
      handleUpdateJobLocations(updatedLocations);
    }
  };

  const getSelectedAndTotalCount = (location: {
    id: LocationType;
    label: string;
  }) => {
    if (location.id === LocationType.TOP_CITIES) {
      const selectedCount = jobLocations?.filter(
        (item) => item.parent === LocationType.TOP_CITIES
      )?.length;
      return `(${selectedCount || 0}/${data?.topCities?.length || 0})`;
    }

    if (location.id === LocationType.INTERNATIONAL_LOCATIONS) {
      const selectedCount = jobLocations?.filter(
        (item) => item.parent === LocationType.INTERNATIONAL_LOCATIONS
      )?.length;
      return `(${selectedCount || 0}/${
        data?.internationalLocations?.length || 0
      })`;
    }

    if (location.id === LocationType.REGION_AND_STATES) {
      const selectedCount = jobLocations?.filter(
        (item) =>
          item.parent &&
          item.parent !== LocationType.INTERNATIONAL_LOCATIONS &&
          item.parent !== LocationType.TOP_CITIES
      )?.length;
      return `(${selectedCount || 0}/${data?.regionAndStates?.length || 0})`;
    }

    return "";
  };

  const getSelectedAndTotalCountForSubList = ({
    id,
    name,
    cities,
  }: {
    id: number;
    name: string;
    cities: {
      id: number;
      value: string;
    }[];
  }) => {
    const selectedCount = jobLocations?.filter(
      (item) => item.parent === `${id}`
    )?.length;

    return `(${selectedCount || 0}/${cities.length || 0})`;
  };

  useEffect(() => {
    if (data?.topCities?.length) {
      setActiveList(LocationType.TOP_CITIES);
    } else if (data?.internationalLocations?.length) {
      setActiveList(LocationType.INTERNATIONAL_LOCATIONS);
    } else if (data?.regionAndStates?.length) {
      setActiveList(LocationType.REGION_AND_STATES);
      // set sub list to expand it
      setActiveSubList({
        id: data?.regionAndStates?.[0]?.id,
        name: data?.regionAndStates?.[0]?.name,
      });
    }
  }, [
    data?.internationalLocations?.length,
    data?.regionAndStates,
    data?.regionAndStates?.length,
    data?.topCities?.length,
  ]);

  if (
    !data?.regionAndStates?.length &&
    !data?.internationalLocations?.length &&
    !data?.topCities?.length
  ) {
    return null;
  }

  return (
    <Grid
      container
      border={`1px solid ${COLOR_MAP.GREY}`}
      position="absolute"
      borderRadius="0.4rem"
      style={{ backgroundColor: "white", zIndex: "20" }}
    >
      <Grid item xs={5} sx={{ borderRight: "1px solid #ccc" }}>
        <List>
          {locations?.map((location) => {
            const isTopCities =
              location.id === LocationType?.TOP_CITIES
                ? data?.topCities?.length
                : true;
            const isInternationalLocations =
              location.id === LocationType?.INTERNATIONAL_LOCATIONS
                ? data?.internationalLocations?.length
                : true;
            const isRegionAndStates =
              location.id === LocationType?.REGION_AND_STATES
                ? data?.regionAndStates?.length
                : true;

            if (
              !isTopCities ||
              !isInternationalLocations ||
              !isRegionAndStates
            ) {
              return null;
            }

            return (
              <ListItemButton
                key={location.id}
                onClick={() => handleActiveList(`${location.id}`)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  backgroundColor:
                    activeList === `${location.id}`
                      ? COLOR_MAP.GREY_8
                      : "unset",
                  py: 0.5,
                }}
                disableRipple
              >
                <ListItemText>
                  {location.label} {getSelectedAndTotalCount(location)}
                </ListItemText>
                <ChevronRightIcon sx={{ ml: 1 }} />
              </ListItemButton>
            );
          })}
        </List>
      </Grid>

      <Grid item xs={7}>
        <List sx={{ overflow: "auto" }}>
          {/* Top cities  */}
          {activeList === LocationType.TOP_CITIES &&
            data?.topCities?.map((city) => {
              return (
                <ListItemButton
                  key={city.id}
                  onClick={() =>
                    handleSelectLocation({
                      id: `${city.id}`,
                      name: city.value,
                      parent: LocationType.TOP_CITIES,
                    })
                  }
                  disableRipple
                  sx={{
                    backgroundColor:
                      activeList === `${city.id}` ? "#F6F6F7" : "unset",
                    py: 0.5,
                  }}
                  disabled={
                    isCheckboxDisabled &&
                    !jobLocations?.find((item) => item.id === `${city.id}`)
                  }
                >
                  <Checkbox
                    sx={{
                      p: 0,
                      "& svg": {
                        color: `${COLOR_MAP.DARK_PURPLE} !important`,
                        height: "1.75rem",
                      },
                      mr: 1,
                    }}
                    checked={
                      !!jobLocations?.find((item) => item.id === `${city.id}`)
                    }
                  />
                  <ListItemText>{city.value}</ListItemText>
                </ListItemButton>
              );
            })}

          {/* International Locations  */}
          {activeList === LocationType.INTERNATIONAL_LOCATIONS &&
            data?.internationalLocations?.map((location) => {
              return (
                <ListItemButton
                  key={location.id}
                  onClick={() =>
                    handleSelectLocation({
                      id: `${location.id}`,
                      name: location.value,
                      parent: LocationType.INTERNATIONAL_LOCATIONS,
                    })
                  }
                  disableRipple
                  sx={{
                    backgroundColor:
                      activeList === `${location.id}` ? "#F6F6F7" : "unset",
                    py: 0.5,
                  }}
                  disabled={
                    isCheckboxDisabled &&
                    !jobLocations?.find((item) => item.id === `${location.id}`)
                  }
                >
                  <Checkbox
                    sx={{
                      p: 0,
                      "& svg": {
                        color: `${COLOR_MAP.DARK_PURPLE} !important`,
                        height: "1.75rem",
                      },
                      mr: 1,
                    }}
                    checked={
                      !!jobLocations?.find(
                        (item) => item.id === `${location.id}`
                      )
                    }
                  />
                  <ListItemText>{location.value}</ListItemText>
                </ListItemButton>
              );
            })}

          {/* Region and states  */}
          {activeList === LocationType.REGION_AND_STATES &&
            !activeSubList &&
            data?.regionAndStates?.map((regionAndState) => {
              const { id, name } = regionAndState;
              return (
                <ListItemButton
                  key={id}
                  onClick={() => handleActiveSubList({ id, name })}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.5,
                  }}
                  disableRipple
                >
                  <ListItemText>
                    {name} {getSelectedAndTotalCountForSubList(regionAndState)}
                  </ListItemText>
                  <ChevronRightIcon sx={{ ml: 1 }} />
                </ListItemButton>
              );
            })}

          {/* Region and states - sub_list */}
          {/* For Heading  */}
          {activeList === LocationType.REGION_AND_STATES &&
            !!activeSubList &&
            !!data?.regionAndStates?.length && (
              <ListItemButton
                key={activeSubList.id}
                onClick={() => handleActiveSubList(null)}
                disableRipple
                sx={{ backgroundColor: "#F6F6F7", py: 0.5 }}
              >
                <KeyboardArrowLeftIcon sx={{ mr: 1 }} />

                <ListItemText>{activeSubList.name}</ListItemText>
              </ListItemButton>
            )}
          {activeList === LocationType.REGION_AND_STATES &&
            !!activeSubList &&
            data?.regionAndStates
              ?.filter((item) => item.id === activeSubList.id)?.[0]
              ?.cities?.map((city) => {
                return (
                  <ListItemButton
                    key={city.id}
                    onClick={() =>
                      handleSelectLocation({
                        id: `${city.id}`,
                        name: city.value,
                        parent: `${activeSubList.id}`,
                      })
                    }
                    disableRipple
                    disabled={
                      isCheckboxDisabled &&
                      !jobLocations?.find((item) => item.id === `${city.id}`)
                    }
                    sx={{ py: 0.5 }}
                  >
                    <Checkbox
                      sx={{
                        p: 0,
                        "& svg": {
                          color: `${COLOR_MAP.DARK_PURPLE} !important`,
                          height: "1.75rem",
                        },
                        mr: 1,
                      }}
                      checked={
                        !!jobLocations?.find((item) => item.id === `${city.id}`)
                      }
                    />
                    <ListItemText>{city.value}</ListItemText>
                  </ListItemButton>
                );
              })}
        </List>
      </Grid>
    </Grid>
  );
};

export default React.memo(JobLocationsSelector);
